import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Layer, LayerProps, Source } from 'react-map-gl';
import { Slider } from '@mui/material'

import { Play, Pause, Replay, SkipEnd } from './VideoControls';
import { formatDisplayDateTime, getDaysBetween, getDaysSince } from '../utilities/time';
import { getGenderText, getTravelDistance } from '../utilities/misc';

// Valid-but-empty FeatureCollection. Mapbox-gl treats a geojson source
// initialized with a malformed geometry (e.g. a LineString with 0–1
// coordinates, or a Point with empty coordinates) as errored and will not
// recover that source via subsequent setData calls until it is removed and
// re-added. Always feed the Sources a valid payload so the very first
// addSource on mount succeeds.
const EMPTY_FC = { type: 'FeatureCollection', features: [] } as any;
const MAX_TIMELINE_MARKS = 24;

const configValue: string = (process.env.REACT_APP_biotracker_service as string);

function resolveAssetUrl(path?: string) {
  if (!path) {
    return '';
  }

  if (path.startsWith('http://') || path.startsWith('https://') || path.startsWith('data:')) {
    return path;
  }

  return `${configValue}${path}`;
}

function getTrackingImage(species: any) {
  return resolveAssetUrl(species?.imageUrl || species?.thumbnailUrl || species?.image || species?.thumbnail || '');
}

function buildTimelineMarks(maxValue: number) {
  if (maxValue <= 0) {
    return [];
  }

  if (maxValue <= MAX_TIMELINE_MARKS - 1) {
    return Array.from({ length: maxValue + 1 }, (_, value) => ({ value }));
  }

  const marks = new Set<number>([0, maxValue]);

  for (let index = 1; index < MAX_TIMELINE_MARKS - 1; index += 1) {
    marks.add(Math.round((index * maxValue) / (MAX_TIMELINE_MARKS - 1)));
  }

  return Array.from(marks)
    .sort((left, right) => left - right)
    .map((value) => ({ value }));
}

function buildPathFrame(path: any[], value: number) {
  const filteredPath = path.filter((point) => getDaysBetween(path[0], point) <= value);
  const coordinates = filteredPath.map((point) => [point.longitude >= 0 ? point.longitude : 360 + point.longitude, point.latitude]);
  const totalPts = filteredPath.length;
  const lastCoord = coordinates[coordinates.length - 1];
  const activePoint = filteredPath[filteredPath.length - 1] ?? path[0];

  return {
    dotsGeojson: {
      type: 'FeatureCollection',
      features: filteredPath.map((point, index) => ({
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [point.longitude >= 0 ? point.longitude : 360 + point.longitude, point.latitude]
        },
        properties: { relAge: totalPts > 1 ? index / (totalPts - 1) : 1 }
      }))
    },
    heatmapData: {
      type: 'FeatureCollection',
      features: filteredPath.map((point, index) => ({
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [point.longitude >= 0 ? point.longitude : 360 + point.longitude, point.latitude]
        },
        properties: {
          weight: totalPts > 1 ? 0.55 + (index / (totalPts - 1)) * 0.45 : 1
        }
      }))
    },
    geojson: {
      type: 'FeatureCollection',
      features: coordinates.length >= 2 ? [
        {
          type: 'Feature',
          geometry: {
            type: 'LineString',
            coordinates
          }
        }
      ] : []
    },
    position: lastCoord ? {
      type: 'FeatureCollection',
      features: [
        {
          type: 'Feature',
          geometry: { type: 'Point', coordinates: lastCoord }
        }
      ]
    } : EMPTY_FC,
    date: formatDisplayDateTime(activePoint.date)
  };
}

interface IPathProps {
  selected: any,
  species: any,
  path: Array<any>,
  color: string,
  onExitPath: any,
  onHideOthers: (hide: boolean) => void
}

function InfoBox(props: any) {
  const [lastPing, setLastPing] = useState(0);
  const [distance, setDistance] = useState(0);
  const [duration, setDuration] = useState(0);
  const [gender, setGender] = useState('Unknown');

  useEffect(() => {
    setLastPing(getDaysSince(props.selected));
    setDistance(getTravelDistance(props.path));
    setDuration(getDaysSince(props.selected.tagged));
    setGender(getGenderText(props.selected));
  }, [props.path, props.selected]);

  const lastPingText = lastPing > 365
    ? `${Math.floor(lastPing / 365)} year${lastPing > 730 ? 's' : ''} ago`
    : `${lastPing} days ago`;
  const avgSpeed = duration > lastPing
    ? (distance / (duration - lastPing) / 24).toFixed(1)
    : '0.0';
  const showSize = props.selected.size && props.selected.size !== 'Unknown' && props.selected.size !== 0;
  const showGender = gender && gender !== 'Unknown';
  const subText = [showGender ? gender : null, showSize ? `${props.selected.size}cm` : null].filter(Boolean).join(', ');

  const trackingImage = getTrackingImage(props.species);
  const headerStyle = trackingImage ? { backgroundImage: `url(${trackingImage})` } : undefined;

  return (
    <div className='info-box apm-card apm-card--tracking'>
      {trackingImage ? (
        <div className="apm-header apm-header--tracking">
          <div className="apm-header-image apm-header-image--tracking" style={headerStyle} />
          <div className="apm-identity apm-identity--tracking">
            <div className="apm-tracking-title-column">
              <span className="apm-name">{props.selected.name}</span>
              <span className="apm-species">{props.selected.species}</span>
            </div>
            <div className="apm-tracking-detail-column">
              {subText && <span className="apm-sub">{subText}</span>}
              <span className="apm-ping">Last Ping: {lastPingText}</span>
            </div>
          </div>
        </div>
      ) : (
        <div className="apm-identity apm-identity--tracking apm-identity--tracking-fallback">
          <div className="apm-tracking-title-column">
            <span className="apm-name">{props.selected.name}</span>
            <span className="apm-species">{props.selected.species}</span>
          </div>
          <div className="apm-tracking-detail-column">
            {subText && <span className="apm-sub">{subText}</span>}
            <span className="apm-ping">Last Ping: {lastPingText}</span>
          </div>
        </div>
      )}
      <div className="apm-body apm-body--tracking">
        <div className="apm-stats apm-stats--compact apm-stats--tracking-inline">
          <div className="apm-stat">
            <span className="apm-stat-label">Tracked</span>
            <span className="apm-stat-value">{duration}<small>d</small></span>
          </div>
          <div className="apm-stat">
            <span className="apm-stat-label">Distance</span>
            <span className="apm-stat-value">{distance.toFixed(0)}<small>kms</small></span>
          </div>
          <div className="apm-stat">
            <span className="apm-stat-label">Avg. Speed</span>
            <span className="apm-stat-value">{avgSpeed}<small>km/h</small></span>
          </div>
        </div>
        <div className="apm-tracking-actions">
          <button className="apm-button apm-button--primary apm-button--tracking" onClick={props.onExitPath}>
            Exit Tracking
          </button>
        </div>
      </div>
    </div>
  )
}

export default function Path(props: IPathProps) {
  const path = useMemo(() => props.path.slice().reverse(), [props.path]);
  const initialFrame = useMemo(() => buildPathFrame(path, 0), [path]);
  const [geojson, setGeoJson] = useState<any>(initialFrame.geojson)
  const [position, setPosition] = useState<any>(initialFrame.position)
  const [date, setDate] = useState(initialFrame.date)

  const [idx, setIdx] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [isFinished, setIsFinished] = useState(false);
  const [speed, setSpeed] = useState(1);
  const [showLine, setShowLine] = useState(true);
  const [showDot, setShowDot] = useState(false);
  const [showHeatmap, setShowHeatmap] = useState(false);
  const [hideOthers, setHideOthers] = useState(true);
  const [dotsGeojson, setDotsGeojson] = useState<any>(initialFrame.dotsGeojson);
  const [heatmapData, setHeatmapData] = useState<any>(initialFrame.heatmapData);

  useEffect(() => {
    props.onHideOthers(true);
    return () => props.onHideOthers(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pathLayer: LayerProps = useMemo(() => ({
    id: 'line-animation',
    type: 'line',
    layout: { visibility: showLine ? 'visible' : 'none' },
    paint: {
      'line-color': props.color,
      'line-width': 2,
      'line-opacity': 0.8
    }
  }), [props.color, showLine]);
  
  const pointLayer: LayerProps = useMemo(() => ({ 
    id: 'point-animation',
    type: 'circle',
    paint: {
      'circle-color': props.color,
      'circle-radius': 8,
      'circle-stroke-width': 2,
      'circle-stroke-color': 'white',
      'circle-stroke-opacity': 0.9
    }
  }), [props.color]);

  const dotsLayer: LayerProps = useMemo(() => ({
    id: 'dots-animation',
    type: 'circle',
    layout: { visibility: showDot ? 'visible' : 'none' },
    paint: {
      'circle-radius': 3.5,
      'circle-color': props.color,
      'circle-opacity': ['interpolate', ['linear'], ['get', 'relAge'], 0, 0.15, 1, 0.85] as any,
      'circle-stroke-width': 1,
      'circle-stroke-color': 'white',
      'circle-stroke-opacity': ['interpolate', ['linear'], ['get', 'relAge'], 0, 0, 0.7, 0, 1, 0.9] as any,
    }
  }), [props.color, showDot]);


  const daysBetween = useMemo(() => {
    return getDaysBetween(props.path[0], props.path[props.path.length - 1])
  }, [props.path])

  const sliderMarks = useMemo(() => buildTimelineMarks(daysBetween), [daysBetween]);

  const heatmapLayer: LayerProps = useMemo(() => ({
    id: 'heatmap-overlay',
    type: 'heatmap',
    layout: { visibility: showHeatmap ? 'visible' : 'none' },
    paint: {
      'heatmap-weight': ['interpolate', ['linear'], ['get', 'weight'], 0, 0.45, 1, 1] as any,
      'heatmap-intensity': ['interpolate', ['linear'], ['zoom'],
        3, 0.3,
        7, 0.7,
        11, 1.6,
        15, 3.5
      ] as any,
      'heatmap-radius': ['interpolate', ['linear'], ['zoom'],
        3, 8,
        7, 28,
        11, 60,
        15, 130
      ] as any,
      'heatmap-opacity': 0.78,
      'heatmap-color': [
        'interpolate', ['linear'], ['heatmap-density'],
        0,    'rgba(0,0,0,0)',
        0.15, 'rgba(255,244,214,0.72)',
        0.35, 'rgba(255,214,102,0.82)',
        0.55, 'rgba(255,159,67,0.9)',
        0.75, 'rgba(236,112,58,0.97)',
        0.9,  'rgba(191,50,73,1)',
        1,    'rgba(112,22,64,1)'
      ] as any
    }
  }), [showHeatmap]);

  const progressSlider = useCallback((value: number) => {
    if (value >= daysBetween) { 
      setIsPlaying(false);
      setIsFinished(true);
    } else {
      setIsFinished(false);
    }

    const frame = buildPathFrame(path, value);

    setDotsGeojson(frame.dotsGeojson);
    setHeatmapData(frame.heatmapData);
    setGeoJson(frame.geojson);
    setPosition(frame.position);
    setDate(frame.date);
    return value
  }, [path, daysBetween])

  useEffect(() => {
    setIsPlaying(true);
    setIsFinished(false);
    setIdx(0);
    progressSlider(0);
  }, [progressSlider])

  useEffect(() => {
    const intervalId = isPlaying 
    ? setInterval(() => {
        setIdx(previousIdx => progressSlider(previousIdx + speed));
      }, 1000/30)
    : undefined;

    return () => clearInterval(intervalId);
  }, [isPlaying, progressSlider, speed])

  function onChange(e: Event, value: number | number[]) {
    setIdx(progressSlider(value as number));
  }

  function onReplay() {
    setIsFinished(false);
    setIsPlaying(false);
    setIdx(progressSlider(0));
  }

  function handlePlayPause() {
    if (isFinished) {
      onReplay();
    } else {
      setIsPlaying(v => !v);
    }
  }

  function onSkipToEnd() {
    setIdx(progressSlider(daysBetween));
  }

  const SPEEDS = [1, 2, 4];
  function onCycleSpeed() {
    setSpeed(s => SPEEDS[(SPEEDS.indexOf(s) + 1) % SPEEDS.length]);
  }

  return (
    <>
    <InfoBox 
      selected={props.selected}
      path={path}
      species={props.species}
      onExitPath={props.onExitPath} />
    <Source id="history-heatmap" type="geojson" data={heatmapData}>
      <Layer {...heatmapLayer} />
    </Source>
    <Source id="history-line" type="geojson" data={geojson}>
      <Layer {...pathLayer} />
    </Source>
    <Source id="history-dots" type="geojson" data={dotsGeojson}>
      <Layer {...dotsLayer} />
    </Source>
    <Source id="history-position" type="geojson" data={position}>
      <Layer {...pointLayer} />
    </Source>
    <div className="history-slider-container">
      <Slider
        className="history-slider"
        aria-label="Timeline"
        defaultValue={0}
        valueLabelDisplay="auto"
        valueLabelFormat={value => date}
        value={idx}
        step={1}
        marks={sliderMarks}
        min={0}
        max={daysBetween}
        onChange={onChange}
      />
      <div className="history-slider-controls">
        <button
          className="hsb-btn hsb-btn--icon"
          onClick={handlePlayPause}
          title={isPlaying ? 'Pause' : (isFinished ? 'Replay' : 'Play')}
        >
          {isPlaying ? <Pause /> : (isFinished ? <Replay /> : <Play />)}
        </button>
        <button className="hsb-btn hsb-btn--icon" onClick={onSkipToEnd} title="Skip to end">
          <SkipEnd />
        </button>
        <button className="hsb-btn hsb-btn--text" onClick={onCycleSpeed} title="Playback speed">
          {speed}x
        </button>
        <span className="history-slider-date">{date}</span>
        <div className="history-slider-spacer" />
        <div className="history-slider-toolbar-group">
          <button
            className={`hsb-btn${showLine ? ' hsb-btn--active' : ''}`}
            onClick={() => { setShowLine(v => !v); setShowHeatmap(false); }}
            title="Toggle path line"
          >
            Line
          </button>
          <button
            className={`hsb-btn${showDot ? ' hsb-btn--active' : ''}`}
            onClick={() => { setShowDot(v => !v); setShowHeatmap(false); }}
            title="Toggle dots"
          >
            Dot
          </button>
          <button
            className={`hsb-btn${showHeatmap ? ' hsb-btn--active' : ''}`}
            onClick={() => { const next = !showHeatmap; setShowHeatmap(next); if (next) { setShowLine(false); setShowDot(false); } }}
            title="Show heatmap"
          >
            Heatmap
          </button>
          <button
            className={`hsb-btn${!hideOthers ? ' hsb-btn--active' : ''}`}
            onClick={() => {
              const next = !hideOthers;
              setHideOthers(next);
              props.onHideOthers(next);
            }}
            title="Toggle other animals"
          >
            Others
          </button>
        </div>
      </div>
    </div>
    </>
  )
}